import React, { useContext, useEffect, useState } from 'react';

import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';

import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';

import { toast } from 'react-toastify';

import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useProvisionService from '../../app/services/hooks/useProvisionService';
import { Provision, ProvisionFilters } from '../../app/models/Provision';
import ProvisionDataTable from './components/tables/ProvisionDataTable';
import ProvisionCreate from './components/subpages/ProvisionCreate';
import ProvisionEdit from './components/subpages/ProvisionEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Previsiones',
        url: '/Provisions',
        isActive: true
    }
];

const Provisions = () => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetProvisions, getProvisions, deleteProvision, activeProvision } =
        useProvisionService();
    const [Provisions, setProvisions] = useState<Provision[]>([]);
    const { navigationPage } = useNavigationPage();

    const [provisionIdEdit, setprovisionIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [ProvisionFilter, setProvisionFilter] = useState<ProvisionFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [ProvisionFilter]);

    const getAllProvisions = () => {
        getProvisions(ProvisionFilter, {
            onSuccess: (response: ServiceResponse) => {
                setProvisions(response.data.provisions);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (provisionId: number) => {
        setShowingEdit(true);
        setprovisionIdEdit(provisionId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setprovisionIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllProvisions();
    };

    const destroy = (provisionId: number) => {
        const _text = 'Está a punto de eliminar la previsión #' + provisionId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando previsión...');
                        deleteProvision(provisionId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La previsión no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (provisionId: number, active: boolean) => {
        activeProvision(provisionId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Previsiones" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva Previsión" />}
                />
                <ProvisionDataTable
                    provisions={Provisions}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetProvisions}
                    filter={ProvisionFilter}
                    setFilter={setProvisionFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear previsión"
                    backdrop={true}
                    showFooter={false}
                >
                    <ProvisionCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar previsión"
                    backdrop={true}
                    showFooter={false}
                >
                    <ProvisionEdit
                        provisionId={provisionIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Provisions;
